import type { ChangeEvent, DragEvent } from "react";
import { useState } from "react";

type UseFileDropOptions = {
  accept?: string[];
  minSize?: number;
  maxSize?: number;
  value?: File | null;
  onValueChange?: (file: File | null) => void;
};

const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const megabytes = bytes / (1024 * 1024);
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB`;
};

export function useFileDrop({ accept, minSize, maxSize, value, onValueChange }: UseFileDropOptions) {
  const [internal, setInternal] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const file = value !== undefined ? value : internal;

  const setFile = (next: File | null) => {
    if (next) {
      if (accept && accept.length > 0 && !accept.includes(next.type)) {
        setLocalError(`File must be one of: ${accept.map((mime) => mime.split("/")[1]?.toUpperCase() ?? mime).join(", ")}`);
        return;
      }
      if (minSize !== undefined && next.size < minSize) {
        setLocalError(`File must be at least ${formatSize(minSize)}`);
        return;
      }
      if (maxSize !== undefined && next.size > maxSize) {
        setLocalError(`File must be under ${formatSize(maxSize)}`);
        return;
      }
    }
    setLocalError(null);
    if (value === undefined) setInternal(next);
    onValueChange?.(next);
  };

  const inputProps = {
    type: "file" as const,
    accept: accept?.join(","),
    className: "hidden",
    onChange: (event: ChangeEvent<HTMLInputElement>) => setFile(event.target.files?.[0] ?? null),
  };

  const dropProps = {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setDragOver(false);
      setFile(event.dataTransfer.files?.[0] ?? null);
    },
  };

  return { file, setFile, dragOver, localError, inputProps, dropProps, formatSize };
}
